import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import type { ReactElement, ReactNode } from 'react';
import { forwardRef, useRef } from 'react';
import type {
  ContainerProps,
  SegmentProps,
  SemanticICONS,
} from 'semantic-ui-react';
import { Container, Header, Icon, Segment } from 'semantic-ui-react';

import ContextPanel from '@components/context-panel';
import TruncateText from '@components/truncate-text';

import Link from '../link';

import { PageProvider, usePageContext } from './page-context';
import { PageContextPanel } from './page-context-panel';
import s from './page.scss';

type PageSegmentProps = SegmentProps;

/**
 * Base component for structural page components.
 */
const PageSegment = forwardRef<HTMLDivElement, PageSegmentProps>(
  ({ className = '', children, ...segmentProps }, forwardedRef) => {
    useStyles(s);

    return (
      <Segment
        ref={forwardedRef}
        className={classNames({
          [s.segment]: true,
          [s.fullBleed]: segmentProps.padded === false,
          [className]: !!className,
        })}
        basic
        padded="very"
        {...segmentProps}
      >
        {children}
      </Segment>
    );
  },
);
PageSegment.displayName = 'PageSegment';

interface PageBoundarySegmentProps extends PageSegmentProps {
  /**
   * Removes the border
   */
  noBorder?: boolean;
  /**
   * Applies sticky positioning, preventing the element from scrolling with the
   * rest of the page content. When applied to a footer, it will stick to the
   * bottom of the viewport. This functionality requires an ancestor to be
   * scrollable and a height of `100vh` - ideally this will be the HTML `body`.
   */
  sticky?: boolean;
}

/**
 * Base component for page boundaries, i.e. headers and footers.
 */
const PageBoundarySegment = forwardRef<
  HTMLDivElement,
  PageBoundarySegmentProps
>(
  (
    {
      className = '',
      children,
      noBorder = false,
      sticky = false,
      ...segmentProps
    },
    forwardedRef,
  ) => {
    useStyles(s);

    return (
      <PageSegment
        ref={forwardedRef}
        className={classNames({
          [s.boundary]: true,
          [s.noBorder]: noBorder,
          [s.sticky]: sticky,
          [className]: !!className,
        })}
        {...segmentProps}
      >
        {children}
      </PageSegment>
    );
  },
);
PageBoundarySegment.displayName = 'PageBoundarySegment';

export interface Breadcrumb {
  title: string;
  path?: string;
}

export interface PageHeaderProps extends PageBoundarySegmentProps {
  /**
   * A breadcrumb navigation element to display above the title.
   *
   * @see Breadcrumb
   */
  breadcrumb?: ReactNode;
  /**
   * @deprecated Use the `breadcrumb` prop with the `Breadcrumb` component
   * instead. This prop renders inline crumbs as part of the title row and will
   * be removed.
   */
  breadcrumbs?: Breadcrumb[];
  /**
   * The page title. Populates a level 1 heading.
   */
  title?: ReactNode;
  /**
   * An optional subtitle to display below the title
   */
  subtitle?: ReactNode;
  /**
   * A flexible area below the title/subtitle for additional content that
   * provides further context at a glance, e.g. labels, badges, etc.
   */
  extra?: ReactNode;
  /**
   * An optional icon to display to the left of the title
   */
  icon?: SemanticICONS | ReactElement;
  /**
   * An optional class name to apply to the icon
   */
  iconClassName?: string;
  /**
   * A navigation element to display at the bottom of the header, below
   * all other content. Typically a `SubNavMenu` component for tab navigation.
   */
  nav?: ReactNode;
  /**
   * An area on the right side of the header suitable for action buttons
   */
  actions?: ReactNode;
}

export const PageHeader = ({
  className = '',
  children,
  actions,
  breadcrumb,
  breadcrumbs,
  extra,
  icon,
  iconClassName,
  nav,
  subtitle,
  title,
  ...rest
}: PageHeaderProps) => {
  useStyles(s);

  const { headerRef } = usePageContext();

  const hasBreadcrumbs = !!breadcrumbs?.length;
  // Complex JSX titles/subtitles manage their own width — bypass TruncateText.
  const isPlainTitle = typeof title === 'string' || typeof title === 'number';
  const isPlainSubtitle =
    typeof subtitle === 'string' || typeof subtitle === 'number';

  // Allow full layout replacement with a custom implementation via children
  const layout = children ?? (
    <div className={s.defaultLayout}>
      <div className={s.breadcrumb} data-testid="page-header-breadcrumb">
        {breadcrumb}
      </div>

      <div className={s.left}>
        <Header as="hgroup" className={s.heading}>
          <Header.Content
            as="h1"
            className={classNames(
              s.title,
              hasBreadcrumbs && s.hasBreadcrumbs,
              !isPlainTitle && s.complexTitle,
            )}
          >
            {icon && (
              <span className={classNames(s.icon, iconClassName)}>
                {typeof icon === 'string' ? (
                  <Icon color="teal" name={icon} />
                ) : (
                  icon
                )}
              </span>
            )}

            {breadcrumbs?.map(item => (
              <div key={`breadcrumb-item-${item.title}`}>
                {item.path ? (
                  <Link to={item.path}>{item.title}</Link>
                ) : (
                  item.title
                )}
                <Icon
                  color="teal"
                  size="small"
                  name="triangle right"
                  className={s.caret}
                />
              </div>
            ))}

            {isPlainTitle ? (
              <span className={s.titleText}>
                <TruncateText position="top left">{title}</TruncateText>
              </span>
            ) : (
              title
            )}
          </Header.Content>

          {subtitle && (
            <Header.Subheader
              as="p"
              className={classNames(
                s.subtitle,
                !isPlainSubtitle && s.subtitleClamp,
              )}
            >
              {isPlainSubtitle ? (
                <TruncateText multiline position="top left">
                  {subtitle}
                </TruncateText>
              ) : (
                subtitle
              )}
            </Header.Subheader>
          )}
        </Header>

        {extra && <div className={s.extra}>{extra}</div>}
      </div>

      <div className={s.right}>{actions}</div>
    </div>
  );

  return (
    <PageBoundarySegment
      as="header"
      ref={headerRef}
      className={classNames({
        [s.header]: true,
        [className]: !!className,
      })}
      {...rest}
    >
      {layout}
      {nav && <div className={s.nav}>{nav}</div>}
    </PageBoundarySegment>
  );
};

export type PageFooterProps = PageBoundarySegmentProps;

export const PageFooter = ({
  className = '',
  children,
  ...rest
}: PageFooterProps) => {
  useStyles(s);

  const { footerRef } = usePageContext();

  return (
    <PageBoundarySegment
      as="footer"
      ref={footerRef}
      className={classNames({
        [s.footer]: true,
        [className]: !!className,
      })}
      {...rest}
    >
      {children}
    </PageBoundarySegment>
  );
};

export interface PageContentProps extends PageSegmentProps {
  /**
   * Prevents the element from growing to fill available vertical space in the
   * flex container, hugging its content instead.
   */
  hug?: boolean;
  /**
   * Prevents the top padding from collapsing when the element follows another
   * padded PageContent.
   */
  noCollapse?: boolean;
}

export const PageContent = ({
  className = '',
  children,
  hug = false,
  noCollapse = false,
  ...rest
}: PageContentProps) => {
  useStyles(s);

  return (
    <PageSegment
      className={classNames({
        [s.content]: true,
        [s.hug]: hug,
        [s.noCollapse]: noCollapse,
        [className]: !!className,
      })}
      {...rest}
    >
      {children}
    </PageSegment>
  );
};

export interface PageProps extends Pick<
  ContainerProps,
  'children' | 'className'
> {
  /**
   * Skeleton mode provides a pass-through structural container without setting
   * the global style overrides or a background color. This can be useful when
   * wrapping existing layouts that doesn't use Page components.
   */
  skeleton?: boolean;
}

/**
 * Main page container and namespace wrapper for all Page components.
 */
const PageContainer = ({ className = '', children, skeleton }: PageProps) => {
  useStyles(s);

  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  return (
    <PageProvider value={{ containerRef, headerRef, footerRef }}>
      <ContextPanel.Provider scope="page">
        <Container
          as="main"
          ref={containerRef}
          className={classNames({
            page: true,
            [s.page]: true,
            [s.skeleton]: skeleton,
            [className]: !!className,
          })}
          fluid
        >
          {children}
        </Container>

        <PageContextPanel />
      </ContextPanel.Provider>
    </PageProvider>
  );
};

export type PageComponent = typeof PageContainer & {
  /**
   * A top-level header section for use within a `Page` component to identify
   * the current document. Provides a consistent layout with flexible content
   * areas. Requires at least a `title`, which will be rendered as a level 1
   * heading.
   *
   * Optionally allows full layout replacement within the containing element via
   * children, if a custom implementation is needed.
   */
  Header: typeof PageHeader;
  /**
   * A primary content area for use within a `Page` component. Grows to fill
   * available vertical space in the viewport, unless disabled with the `hug`
   * prop.
   */
  Content: typeof PageContent;
  /**
   * A footer section for use within a `Page` component. The footer is always
   * positioned at the bottom boundary of the page container - If the main page
   * content does not fill the viewport height, the body grows to fill the space
   * and pushes the footer to the bottom. If the content exceeds the viewport,
   * the footer will be pushed out of view, but can optionally be made sticky.
   */
  Footer: typeof PageFooter;
};

/**
 * A generic structural component for consistent page layouts.
 *
 * `Page` is a root container and namespace, providing child components for
 * header, body, and footer sections. The child components are not designed to
 * be used outside of a `Page` container.
 *
 * This is intended to be used as the main content container for a page. It
 * fills the available viewport and expects a parent container to handle scroll
 * overflow, e.g. `body`.
 *
 * @example
 * <Page>
 *   <Page.Header
 *     title="Page Title"
 *     subtitle="A brief description of the page content"
 *     actions={<Button primary>Primary Action</Button>}
 *   />
 *   <Page.Content>Hello, world!</Page.Content>
 *   <Page.Footer>Footer content</Page.Footer>
 * </Page>
 */
export const Page = Object.assign(PageContainer, {
  Header: PageHeader,
  Content: PageContent,
  Footer: PageFooter,
}) as PageComponent;

export default Page;
